<template>
    <div class="eval-stats">
        <button class="stats-back-btn" @click="$router.back()">
            <v-icon size="16">mdi-chevron-left</v-icon>
            {{ $t('common.back') }}
        </button>

        <skeleton-loader v-if="loading" type="eval-stats" />

        <template v-else>
            <div class="stats-top">
                <div class="student-card">
                    <div class="student-card-photo">
                        <student-avatar :avatar="student.avatar" :name="student.name" :avatar-key="studentId"
                            :size="56" :radius="28" :font-size="18" />
                    </div>
                    <div class="student-card-body">
                        <div class="student-card-name">{{ student.name }}</div>
                        <div v-for="field in studentFields" :key="field.label" class="student-card-field">
                            <span class="student-card-field-label">{{ field.label }}</span>
                            <span class="student-card-field-value" :class="{ 'value-accent': field.accent }">
                                {{ field.value }}
                            </span>
                        </div>
                        <button v-if="hasData" class="stats-export-btn" @click="exportExcel">
                            <v-icon size="14">mdi-tray-arrow-down</v-icon>
                            {{ $t('common.export') }}
                        </button>
                    </div>
                </div>

                <div v-if="!hasData" class="stats-empty-card">
                    <v-icon size="40" color="txtMuted">mdi-database-off-outline</v-icon>
                    <p>{{ $t('common.no_values_student') }}</p>
                </div>

                <div v-else class="stats-table-card">
                    <div class="stats-table-scroll">
                        <table class="stats-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('common.evaluation') }}</th>
                                    <th>{{ $t('common.unit_measure') }}</th>
                                    <th>{{ $t('common.actual') }}</th>
                                    <th>{{ $t('common.difference_ideal') }}</th>
                                    <th>{{ $t('common.best') }}</th>
                                    <th>{{ $t('common.worst') }}</th>
                                    <th>{{ $t('common.average') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in items" :key="item.id">
                                    <td>
                                        <div class="metric-name-cell"><strong>{{ item.name }}</strong></div>
                                    </td>
                                    <td>{{ item.unit }}</td>
                                    <td>{{ item.actual }}</td>
                                    <td>
                                        <div class="diff-cell">
                                            <span :class="diffClass(item)">{{ diffText(item) }}</span>
                                            <div class="diff-bar-wrap">
                                                <div class="diff-bar-fill" :style="diffBarStyle(item)" />
                                            </div>
                                        </div>
                                    </td>
                                    <td>{{ item.best }}</td>
                                    <td>{{ item.worst }}</td>
                                    <td>{{ item.average }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="hasData" class="stats-charts">
                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.historical') }}</span>
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <button class="chart-select" v-bind="attrs" v-on="on">
                                    {{ currentConfigDates ? currentConfigDates.name : '' }}
                                    <span class="chart-select-caret">▾</span>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item v-for="metric in configWithData" :key="metric.id" link color="primary"
                                    :input-value="currentConfigDates && currentConfigDates.id === metric.id"
                                    @click="currentConfigDates = metric">
                                    <v-list-item-title>{{ metric.name }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div class="chart-body">
                        <evaluation-dates-chart :values="currentValuesDates"
                            :ideal="currentConfigDates ? currentConfigDates.value : null" style="height: 210px;" />
                        <div class="chart-legend-row">
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-success" />
                                {{ $t('common.actual') }}
                            </div>
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-accent" />
                                {{ $t('common.ideal') }}: {{ currentConfigDates ? currentConfigDates.value : '' }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.summary_evaluations') }}</span>
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <button class="chart-select" v-bind="attrs" v-on="on">
                                    {{ currentConfig ? currentConfig.name : '' }}
                                    <span class="chart-select-caret">▾</span>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item v-for="metric in configWithData" :key="metric.id" link color="primary"
                                    :input-value="currentConfig && currentConfig.id === metric.id"
                                    @click="currentConfig = metric">
                                    <v-list-item-title>{{ metric.name }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div class="chart-body">
                        <evaluation-summary-chart v-if="currentItem" :item="currentItem" />
                    </div>
                </div>

                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.profile_evaluations') }}</span>
                    </div>
                    <div class="chart-body">
                        <radar-chart :labels="evaluationsName" :datasets="evaluationData" style="height: 230px;" />
                        <div class="chart-legend-row">
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-primary" />
                                {{ $t('common.actual') }}
                            </div>
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-accent" />
                                {{ $t('common.ideal') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { db } from "@/utils/Firebase";
import { saveWorkbook } from "@/utils/Export";
import { toDate } from "@/utils/Common";
import { mapActions, mapState } from "vuex";
import RadarChart from "@/components/common/RadarChart";
import EvaluationDatesChart from '../common/EvaluationDatesChart.vue';
import EvaluationSummaryChart from '../common/EvaluationSummaryChart.vue';
import StudentAvatar from '../common/StudentAvatar.vue';
import SkeletonLoader from '../common/SkeletonLoader.vue';
import { getCurrentValuesDatesForEvaluation, getDatasetsForEvaluation, getItemsForEvaluation, getValuesForEvaluation } from "../../utils/Evaluations";

export default {
    name: "EvaluationStatisticsStudent",
    components: { RadarChart, EvaluationDatesChart, EvaluationSummaryChart, StudentAvatar, SkeletonLoader },
    data() {
        return {
            loading: true,
            student: null,
            assignedEvaluation: null,
            currentConfig: null,
            currentConfigDates: null,
        }
    },
    computed: {
        ...mapState(['evaluations', 'libraryCoach']),
        coachId(vm = this) {
            return vm.isCompany ? vm.libraryCoach?.id || null : vm.currentUser?.uid || null
        },
        hasValues(vm = this) {
            return vm.assignedEvaluation?.values || false
        },
        studentId(vm = this) {
            return vm.$route.params.id
        },
        evaluationId(vm = this) {
            return vm.assignedEvaluation?.evaluationId || null
        },
        currentEvaluation(vm = this) {
            if (!vm.evaluationId) return null
            return vm.evaluations.find(eva => eva.id === vm.evaluationId) || null
        },
        config(vm = this) {
            return vm.currentEvaluation?.config || []
        },
        configWithData(vm = this) {
            return vm.config.filter(c => vm.items.some(item => item.id === c.id))
        },
        hasData(vm = this) {
            return vm.items.length > 0
        },
        values(vm = this) {
            if (!vm.hasValues) return []
            return getValuesForEvaluation(vm.assignedEvaluation?.values)
        },
        items(vm = this) {
            if (!vm.config || !vm.values) return []
            return getItemsForEvaluation(vm.config, vm.values)
        },
        currentItem(vm = this) {
            if (!vm.currentConfig) return null
            return vm.items.find(item => item.id === vm.currentConfig.id) || null
        },
        currentValuesDates(vm = this) {
            if (!vm.currentConfigDates) return null
            return getCurrentValuesDatesForEvaluation(vm.assignedEvaluation?.values, vm.currentConfigDates)
        },
        evaluationsName(vm = this) {
            return vm.items.map(item => item.name)
        },
        evaluationData(vm = this) {
            if (vm.items.length === 0) return [];
            return getDatasetsForEvaluation(vm.items)
        },
        recordsCount(vm = this) {
            return vm.assignedEvaluation?.values?.length || 0
        },
        studentFields(vm = this) {
            const student = vm.student || {}
            let age = vm.$t('common.not_available_short')
            const birthDate = toDate(student.birthDate)
            if (birthDate) {
                age = `${vm.$moment().diff(vm.$moment(birthDate), 'years')} ${vm.$t('common.years')}`
            }
            return [
                { label: vm.$t('common.age'), value: age },
                { label: vm.$t('common.height'), value: `${student.height || 0} ${vm.$tc('common.cm', 1)}` },
                { label: vm.$t('common.weight'), value: `${student.weight || 0} ${vm.$tc('common.kg', 1)}` },
                { label: vm.$t('common.evaluations'), value: `${vm.recordsCount} ${vm.$tc('common.records', vm.recordsCount)}`, accent: true },
            ]
        },
    },
    async mounted() {
        const vm = this
        if (vm.evaluations.length === 0) await vm.bindEvaluations(vm.coachId)
        await vm.loadAssignedEvaluation()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        async loadAssignedEvaluation() {
            const vm = this
            await vm.withLoading(async () => {
                let ref = await db.collection('users').doc(vm.studentId).get()
                vm.student = { id: ref.id, ...ref.data() }

                let assignedRef = await db.collection('assignedEvaluations').doc(vm.student.assignedEvaluationId).get()
                vm.assignedEvaluation = { id: assignedRef.id, ...assignedRef.data() }
            })
        },
        diffText(item) {
            return `${item.color === 'success' ? '+' : '−'}${item.difference}`
        },
        diffClass(item) {
            return item.color === 'success' ? 'diff-positive' : 'diff-negative'
        },
        diffBarStyle(item) {
            const magnitude = Math.min((parseFloat(item.difference) || 0) * 8, 100)
            return {
                width: `${magnitude}%`,
                background: item.color === 'success' ? 'var(--v-success-base)' : 'var(--v-error-base)',
            }
        },
        async exportExcel() {
            const vm = this
            const XLSX = (await import("xlsx")).default
            const currentValues = vm.assignedEvaluation.values
            let evaluationData = currentValues.map(value => {
                return {
                    [vm.$t('common.date')]: value.date,
                    ...Object.fromEntries(vm.config.map(config => {
                        const key = config.id
                        const val = value[key] ? parseFloat(value[key]?.toString().replace(',', '.')) : null
                        return [config.name, val]
                    }))
                }
            })
            let data = XLSX.utils.json_to_sheet(evaluationData)
            const workbook = XLSX.utils.book_new()
            const filename = vm.student?.name ? `${vm.$t('common.evaluations')} - ${vm.student.name}` : vm.$t('common.evaluations')
            XLSX.utils.book_append_sheet(workbook, data, filename.substring(0, 31))
            await saveWorkbook(XLSX, workbook, filename, vm.$t('common.export'))
        },
    },
    watch: {
        configWithData(val) {
            const validConfig = this.currentConfig && val.some(c => c.id === this.currentConfig.id)
            if (!validConfig) this.currentConfig = val[0] || null
            const validDates = this.currentConfigDates && val.some(c => c.id === this.currentConfigDates.id)
            if (!validDates) this.currentConfigDates = val[0] || null
        }
    }
}
</script>

<style scoped>
.eval-stats {
    padding: 16px 24px;
}

.stats-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 0 12px 0 8px;
    border-radius: 8px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface-base);
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 16px;
    transition: background 0.12s;
    text-transform: capitalize;
}

.stats-back-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.stats-top {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 20px;
}

.student-card {
    width: 240px;
    flex-shrink: 0;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

.student-card-photo {
    width: 100%;
    height: 110px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0D2018 0%, #0D1A2A 100%);
}

.student-card-body {
    padding: 14px 16px;
}

.student-card-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-bottom: 10px;
}

.student-card-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 0;
    border-bottom: 1px solid var(--v-outline-base);
    font-size: 12px;
}

.student-card-field:last-of-type {
    border-bottom: none;
}

.student-card-field-label {
    color: var(--v-txtMuted-base);
}

.student-card-field-value {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
}

.student-card-field-value.value-accent {
    color: var(--v-primary-base);
}

.theme--light .student-card-field-value.value-accent {
    color: var(--v-accent-base);
}

.stats-export-btn {
    width: 100%;
    height: 34px;
    border-radius: 8px;
    margin-top: 12px;
    background: rgba(204, 255, 51, 0.08);
    border: 1px solid rgba(204, 255, 51, 0.18);
    color: var(--v-primary-base);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.12s;
}

.stats-export-btn:hover {
    background: rgba(204, 255, 51, 0.16);
}

.theme--light .stats-export-btn {
    background: rgba(51, 153, 153, 0.08);
    border-color: rgba(51, 153, 153, 0.25);
    color: var(--v-accent-base);
}

.stats-empty-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
    text-align: center;
}

.stats-table-card {
    flex: 1;
    min-width: 0;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

.stats-table-scroll {
    overflow-x: auto;
}

.stats-table {
    width: 100%;
    border-collapse: collapse;
}

.stats-table thead tr {
    border-bottom: 1px solid var(--v-outline-base);
}

.stats-table thead th {
    padding: 9px 14px;
    text-align: right;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.07em;
    background: linear-gradient(180deg, var(--v-surface2-base) 0%, var(--v-surface-base) 100%);
    white-space: nowrap;
}

.stats-table thead th:first-child {
    text-align: left;
}

.stats-table thead th:nth-child(2) {
    text-align: center;
}

.stats-table tbody tr {
    border-bottom: 1px solid var(--v-outline-base);
    transition: background 0.1s;
}

.stats-table tbody tr:last-child {
    border-bottom: none;
}

.stats-table tbody tr:hover {
    background: var(--v-surface2-base);
}

.stats-table td {
    padding: 10px 14px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    text-align: right;
    vertical-align: middle;
    white-space: nowrap;
}

.stats-table td:first-child {
    text-align: left;
}

.stats-table td:nth-child(2) {
    text-align: center;
    color: var(--v-txtMuted-base);
    font-size: 11px;
}

.metric-name-cell strong {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.diff-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.diff-bar-wrap {
    width: 80px;
    height: 4px;
    background: var(--v-outline2-base);
    border-radius: 2px;
    overflow: hidden;
}

.diff-bar-fill {
    height: 100%;
    border-radius: 2px;
}

.diff-positive {
    color: var(--v-success-base);
    font-weight: 700;
    font-size: 12px;
}

.diff-negative {
    color: var(--v-error-base);
    font-weight: 700;
    font-size: 12px;
}

.stats-charts {
    display: grid;
    grid-template-columns: 1.35fr 1fr 0.85fr;
    gap: 14px;
}

.chart-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

.chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 14px 4px 14px;
}

.chart-title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    color: var(--v-primary-base);
}

.theme--light .chart-title {
    color: var(--v-accent-base);
}

.chart-select {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: none;
    border: none;
    outline: none;
    color: var(--v-primary-base);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.theme--light .chart-select {
    color: var(--v-accent-base);
}

.chart-select-caret {
    font-size: 9px;
    line-height: 1;
}

.chart-body {
    padding: 4px 12px 12px 12px;
}

.chart-legend-row {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 6px;
}

.chart-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.chart-legend-dot {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    flex-shrink: 0;
}

.dot-success {
    background: var(--v-success-base);
}

.dot-primary {
    background: var(--v-primary-base);
}

.dot-accent {
    background: var(--v-accent-base);
}

@media (max-width: 1100px) {
    .stats-charts {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 860px) {
    .stats-top {
        flex-direction: column;
    }

    .student-card {
        width: 100%;
    }
}

@media (max-width: 700px) {
    .stats-charts {
        grid-template-columns: 1fr;
    }
}
</style>
